import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateProduct } from '../store/productSlice';
import { PW_WinningOptions_Save_API } from '../Server/ApiV2';
import { PICK6_MAX_OPTIONS, PICK6_MAX_WINNERS, validateOptions, winnerProblems } from '../utils/pick6';

const fmt = (v) => (Math.round(Number(v) * 100) / 100).toLocaleString();
const toRow = (o) => ({ prize: String(o.prize) });

// A Pick 6 product's winning options — the prizes every coupon prints, each with
// a winning number drawn for that coupon — on the Products page, under its
// Linked Types (which set how many coupons win each prize). Prizes are picked
// from the product's denominations when it has any.
export default function Pick6Options({ product, denominations = [], links = [] }) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState(null); // null = not editing
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const editing = rows !== null;
  const shown = editing ? rows : (product.winningOptions || []);
  const factor = product.priceAmount || 1;
  const problems = editing ? validateOptions(rows, denominations) : [];
  const unprintable = winnerProblems(links, shown);

  const startEdit = () => {
    setRows((product.winningOptions || []).map(toRow));
    setTried(false);
    setError('');
  };
  const change = (i, field, value) => setRows(prev => prev.map((r, k) => (k === i ? { ...r, [field]: value } : r)));

  const save = async () => {
    setTried(true);
    // Options a type's winners can't be printed on are refused by the server too.
    if (problems.length || unprintable.length || saving) return;
    setSaving(true);
    setError('');
    const options = rows.map(r => ({ prize: Number(r.prize) }));
    const res = await PW_WinningOptions_Save_API({ productId: product.id, options });
    setSaving(false);
    if (!res?.data?.success) {
      setError(res?.response?.data?.message
        ? `${res.response.data.message} Nothing was changed.`
        : 'Could not reach the server. Nothing was changed.');
      return;
    }
    dispatch(updateProduct({ id: product.id, winningOptions: res.data.data }));
    setRows(null);
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div className="text-sm fw-600 mb-8" style={{ color: '#2d1b69', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>Winning Options ({shown.length} of {PICK6_MAX_OPTIONS})</span>
        {!editing && (
          <button className="btn btn-outline btn-sm" style={{ padding: '2px 10px', fontSize: 11 }} onClick={startEdit}>Edit Options</button>
        )}
      </div>
      <div className="text-sm text-muted mb-8">
        Every coupon prints these prizes, each with a winning number drawn for that coupon after the customer picks.
        The types above set how many times each prize is won per 100 coupons, up to {PICK6_MAX_WINNERS} in all; each
        winner is one winning coupon paying that prize. 40% of the winning coupons win on one of the customer's
        numbers; 60% win on several, the prize split into as many of these option prizes as it can be (each on a
        different number and option) — a prize that can't be split (5) always pays on one number. Paid × Factor ({factor}).
      </div>
      <div className="table-wrap">
        <table className="winner-config-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Prize</th>
              <th>Paid (× {factor})</th>
              {editing && <th></th>}
            </tr>
          </thead>
          <tbody>
            {shown.map((o, i) => (
              <tr key={i}>
                <td className="text-muted">{i + 1}</td>
                <td>
                  {editing && denominations.length > 0 ? (
                    <select className="form-select" value={o.prize} aria-label={`Option ${i + 1} prize`}
                      style={{ width: 110, padding: '5px 8px', fontSize: 13 }}
                      onChange={e => change(i, 'prize', e.target.value)}>
                      <option value="">Choose</option>
                      {o.prize !== '' && !denominations.includes(Number(o.prize)) && (
                        <option value={o.prize}>{fmt(o.prize)} (not listed)</option>
                      )}
                      {denominations.map(d => <option key={d} value={String(d)}>{d.toLocaleString()}</option>)}
                    </select>
                  ) : editing ? (
                    <input type="number" min={0} step="any" value={o.prize} aria-label={`Option ${i + 1} prize`} style={{ width: 90 }}
                      onChange={e => change(i, 'prize', e.target.value)} />
                  ) : <span className="fw-600">{fmt(o.prize)}</span>}
                </td>
                <td className="fw-600" style={{ color: '#c9a227' }}>{fmt((Number(o.prize) || 0) * factor)}</td>
                {editing && (
                  <td>
                    <button className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}
                      aria-label={`Remove option ${i + 1}`} onClick={() => setRows(prev => prev.filter((_, k) => k !== i))}>X</button>
                  </td>
                )}
              </tr>
            ))}
            {!shown.length && (
              <tr><td colSpan={editing ? 4 : 3} className="text-sm text-muted">No winning options yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
          <button className="btn btn-outline btn-sm" disabled={rows.length >= PICK6_MAX_OPTIONS || saving}
            onClick={() => setRows(prev => [...prev, { prize: '' }])}>+ Add Option</button>
          <button className="btn btn-success btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save Options'}</button>
          <button className="btn btn-secondary btn-sm" disabled={saving} onClick={() => setRows(null)}>Cancel</button>
          <span className="text-sm text-muted">Saved options apply from the next coupon sold.</span>
        </div>
      )}
      {unprintable.length > 0 && (
        <div className="login-error" style={{ marginTop: 10, marginBottom: 0 }}>
          {unprintable.map(u => (
            <div key={`${u.typeId}-${u.prize}`}>
              {`Type ${u.typeId} has winners for prize ${u.prize.toLocaleString()}, but no option has that prize`}
              {editing
                ? '. Keep an option with that prize, or set those winners to 0 first.'
                : ' — that type can\'t be sold until this is fixed.'}
            </div>
          ))}
        </div>
      )}
      {tried && problems.length > 0 && (
        <div className="login-error" style={{ marginTop: 10, marginBottom: 0 }}>
          {problems.map(t => <div key={t}>{t}</div>)}
        </div>
      )}
      {error && <div className="login-error" style={{ marginTop: 10, marginBottom: 0 }}>{error}</div>}
    </div>
  );
}
